import React, { useMemo } from 'react';
import { buildViewerPaymentSummary } from '../../../utils/paymentSettlement';

/**
 * Payment owed / settlement summary for in-table overlays (casino v2).
 */
export default function PaymentOwedPanel({
  paymentHistory = [],
  allSeats = [],
  creditValue = 1,
  viewerPlayerID,
  tableID,
}) {
  const summary = useMemo(() => {
    if (!viewerPlayerID || !paymentHistory?.length || !allSeats?.length) return [];
    return buildViewerPaymentSummary(allSeats, paymentHistory, viewerPlayerID);
  }, [paymentHistory, allSeats, viewerPlayerID]);

  const history = Array.isArray(paymentHistory) ? paymentHistory : [];
  const hasAnyData = summary.length > 0 || history.length > 0;

  const openFullReport = () => {
    if (!tableID) return;
    const reportUrl = `${window.location.origin}/payment-report/?table=${encodeURIComponent(tableID)}`;
    window.open(reportUrl, '_blank', 'noopener,noreferrer');
  };

  if (!hasAnyData) {
    return (
      <div className="cv2-utility-empty">
        <p className="cv2-utility-empty-title">No payments owed yet</p>
        <p className="cv2-utility-empty-hint">Settlement amounts appear here after winning pots are recorded.</p>
      </div>
    );
  }

  return (
    <div className="cv2-utility-list">
      <div className="cv2-utility-banner">
        Credit value: <strong>${Number(creditValue).toFixed(2)}</strong> per chip
      </div>

      <section className="cv2-utility-section">
        <h3 className="cv2-utility-section-title">Your net summary</h3>
        {summary.length > 0 ? (
          <ul className="cv2-utility-summary-list">
            {summary.map((row) => (
              <li
                key={row.playerID}
                className={`cv2-utility-summary-row ${row.youOweThem > 0 ? 'owe' : 'owed'}`}
              >
                <span className="cv2-utility-summary-name">{row.playerName}</span>
                <span className="cv2-utility-summary-amounts">
                  {row.youOweThem > 0 && (
                    <span className="cv2-text-loss">
                      You owe ${(row.youOweThem * creditValue).toFixed(2)}
                    </span>
                  )}
                  {row.theyOweYou > 0 && (
                    <span className="cv2-text-win">
                      Owes you ${(row.theyOweYou * creditValue).toFixed(2)}
                    </span>
                  )}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="cv2-utility-muted cv2-utility-inline-empty">No outstanding balances with other players.</p>
        )}
      </section>

      <section className="cv2-utility-section">
        <h3 className="cv2-utility-section-title">Payment history</h3>
        {history.length > 0 ? (
          history
            .slice()
            .reverse()
            .map((payment, i) => (
              <article key={`pay-${payment.round}-${i}`} className="cv2-utility-card cv2-utility-card-compact">
                <header className="cv2-utility-card-head">Round {payment.round}</header>
                <dl className="cv2-utility-dl">
                  <div>
                    <dt>Type</dt>
                    <dd className={payment.type === 'win' ? 'cv2-text-win' : 'cv2-text-warn'}>
                      {payment.type === 'win' ? 'Single winner' : 'Tie'}
                    </dd>
                  </div>
                  <div>
                    <dt>Pot</dt>
                    <dd className="cv2-text-accent">
                      {payment.pot} chips (${(payment.pot * creditValue).toFixed(2)})
                    </dd>
                  </div>
                  {payment.type === 'win' ? (
                    <>
                      <div>
                        <dt>Winner</dt>
                        <dd className="cv2-text-win">
                          {allSeats.find((p) => p.playerID === payment.winner)?.name || 'Unknown'}
                        </dd>
                      </div>
                      <div>
                        <dt>Won</dt>
                        <dd className="cv2-text-win">
                          {payment.amount} chips (${(payment.amount * creditValue).toFixed(2)})
                        </dd>
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <dt>Tied players</dt>
                        <dd className="cv2-text-warn">
                          {(payment.players || [])
                            .map((id) => allSeats.find((p) => p.playerID === id)?.name || 'Unknown')
                            .join(', ')}
                        </dd>
                      </div>
                      <div>
                        <dt>Split</dt>
                        <dd className="cv2-text-warn">
                          {payment.amount} chips each (${(payment.amount * creditValue).toFixed(2)})
                        </dd>
                      </div>
                    </>
                  )}
                </dl>
              </article>
            ))
        ) : (
          <p className="cv2-utility-muted cv2-utility-inline-empty">No payment events recorded yet.</p>
        )}
      </section>

      {tableID && (
        <div className="cv2-utility-footer-actions">
          <button type="button" className="cv2-btn ghost cv2-utility-link-btn" onClick={openFullReport}>
            Open full settlement report
          </button>
        </div>
      )}
    </div>
  );
}
